import type { KillBroadcastMsg } from "@hupper/shared/protocol";
import type { GameClient } from "../net/GameClient";
import { killWeaponIcon, killBadgeImg } from "./weaponIcons";

/**
 * KillFeed — top-right CS:GO-style feed of the last few kills, each fading out.
 *
 * Row layout mirrors CS:GO:
 *   [attacker]  <weapon icon> <modifier badges>  [victim]
 *
 * Names are team-colored (CT blue / T orange). The weapon uses the SAME art as
 * the HUD (rendered PNG for guns/knife, color-coded glyph for grenades). Three
 * server-authoritative modifier badges cluster on the weapon:
 *   • headshot  — brain shot (zone head/helmet)
 *   • smoke     — victim died inside an active smoke cloud
 *   • blind     — victim was still flash-blinded when they died
 *
 * Pure-text styling (no card box) with a layered shadow, matching the chat —
 * icons carry their own drop-shadow so they read over the bright arena.
 * `?killdemo=1` previews every badge combination without a live match.
 */
export class KillFeed {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];

  /** Max entries on screen. Older ones drop off. */
  private static readonly MAX_ENTRIES = 5;
  /** Entry lifetime (ms). */
  private static readonly LIFETIME_MS = 8000;
  /** Final fade-out animation (ms). */
  private static readonly FADE_MS = 600;

  constructor(client: GameClient) {
    this.client = client;
    this.root = document.createElement("div");
    this.root.id = "kill-feed";
    document.body.appendChild(this.root);
    this.injectStyles();

    this.unsubs.push(this.client.onKill((msg) => this.addEntry(msg)));
  }

  destroy(): void {
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  /** `?killdemo=1` — seeded feed showing each badge combination. */
  debugPreview(): void {
    const rows: RenderEntry[] = [
      { attackerName: "Nova", attackerTeam: "ct", victimName: "Kzinho", victimTeam: "t", weaponId: "ak47", headshot: true, throughSmoke: false, blindKill: false, noScope: false, suicide: false, isLocalAttacker: true, isLocalVictim: false },
      { attackerName: "Kzinho", attackerTeam: "t", victimName: "Nova", victimTeam: "ct", weaponId: "awp", headshot: true, throughSmoke: false, blindKill: false, noScope: true, suicide: false, isLocalAttacker: false, isLocalVictim: true },
      { attackerName: "Nova", attackerTeam: "ct", victimName: "Kzinho", victimTeam: "t", weaponId: "awp", headshot: true, throughSmoke: true, blindKill: false, noScope: false, suicide: false, isLocalAttacker: true, isLocalVictim: false },
      { attackerName: "Kzinho", attackerTeam: "t", victimName: "Nova", victimTeam: "ct", weaponId: "usp_s", headshot: false, throughSmoke: false, blindKill: true, noScope: false, suicide: false, isLocalAttacker: false, isLocalVictim: true },
      { attackerName: "Nova", attackerTeam: "ct", victimName: "Kzinho", victimTeam: "t", weaponId: "knife", headshot: false, throughSmoke: true, blindKill: true, noScope: false, suicide: false, isLocalAttacker: true, isLocalVictim: false },
      { attackerName: "Nova", attackerTeam: "ct", victimName: "Nova", victimTeam: "ct", weaponId: "he", headshot: false, throughSmoke: false, blindKill: false, noScope: false, suicide: true, isLocalAttacker: false, isLocalVictim: false },
    ];
    rows.forEach((r, i) => window.setTimeout(() => this.render(r), i * 650));
  }

  private addEntry(msg: KillBroadcastMsg): void {
    const attacker = this.client.getPlayerSnapshot(msg.attackerSessionId);
    const victim = this.client.getPlayerSnapshot(msg.victimSessionId);
    const suicide = msg.suicide === true || msg.attackerSessionId === msg.victimSessionId;
    this.render({
      attackerName: attacker?.name ?? "unknown",
      attackerTeam: attacker?.team ?? "ct",
      victimName: victim?.name ?? "unknown",
      victimTeam: victim?.team ?? "t",
      weaponId: msg.weaponId,
      headshot: msg.headshot === true,
      throughSmoke: msg.throughSmoke === true,
      blindKill: msg.blindKill === true,
      noScope: msg.noScope === true,
      suicide,
      isLocalAttacker: !suicide && msg.attackerSessionId === this.client.sessionId,
      isLocalVictim: msg.victimSessionId === this.client.sessionId,
    });
  }

  private render(e: RenderEntry): void {
    const row = document.createElement("div");
    row.className = "kf-row";
    if (e.isLocalAttacker) row.classList.add("kf-local-attacker");
    if (e.isLocalVictim) row.classList.add("kf-local-victim");

    // Modifier badges cluster on the weapon (CS:GO order: smoke, blind,
    // no-scope, headshot).
    const badges =
      (e.throughSmoke ? killBadgeImg("smoke") : "") +
      (e.blindKill ? killBadgeImg("blind") : "") +
      (e.noScope ? killBadgeImg("noscope") : "") +
      (e.headshot ? killBadgeImg("headshot") : "");

    const weapon = `<span class="kf-weap">${killWeaponIcon(e.weaponId)}</span>`;
    const badgeSpan = badges ? `<span class="kf-badges">${badges}</span>` : "";
    const victimSpan = `<span class="kf-name kf-team-${e.victimTeam}">${escapeHtml(e.victimName)}</span>`;

    if (e.suicide) {
      // Self-kill: just the weapon → victim (no attacker name).
      row.innerHTML = `${weapon}${badgeSpan}${victimSpan}`;
    } else {
      const attackerSpan = `<span class="kf-name kf-team-${e.attackerTeam}">${escapeHtml(e.attackerName)}</span>`;
      row.innerHTML = `${attackerSpan}${weapon}${badgeSpan}${victimSpan}`;
    }
    this.root.prepend(row);

    while (this.root.children.length > KillFeed.MAX_ENTRIES) {
      this.root.lastElementChild?.remove();
    }

    window.setTimeout(() => {
      row.classList.add("kf-fade");
      window.setTimeout(() => row.remove(), KillFeed.FADE_MS);
    }, KillFeed.LIFETIME_MS - KillFeed.FADE_MS);
  }

  private injectStyles(): void {
    if (document.getElementById("kill-feed-styles")) return;
    const style = document.createElement("style");
    style.id = "kill-feed-styles";
    style.textContent = `
      #kill-feed {
        position: fixed;
        top: 56px;
        right: 16px;
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 6px;
        z-index: 8;
        pointer-events: none;
        color: #f3ede4; /* warm-white — SVG fallback icons inherit it */
        font-family: ui-monospace, SFMono-Regular, monospace;
        font-size: 14px;
        max-width: 460px;
      }
      /* Pure text, no card — a layered shadow keeps names legible over the
         bright arena, exactly like the chat lines. */
      #kill-feed .kf-row {
        display: flex;
        align-items: center;
        gap: 8px;
        text-shadow:
          0 1px 2px rgba(0, 0, 0, 0.95),
          0 0 3px rgba(0, 0, 0, 0.85),
          0 2px 8px rgba(0, 0, 0, 0.45);
        transition: opacity 600ms ease-out, transform 600ms ease-out;
        animation: kf-line-in 140ms ease-out;
      }
      @keyframes kf-line-in {
        from { opacity: 0; transform: translateX(14px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      #kill-feed .kf-row.kf-fade { opacity: 0; transform: translateX(20px); }
      #kill-feed .kf-name { font-weight: 700; white-space: nowrap; }
      #kill-feed .kf-team-ct { color: #5eb8ff; }
      #kill-feed .kf-team-t  { color: #ff8a5c; }
      /* A kill you're part of: brighten your side + a faint glow so it pops. */
      #kill-feed .kf-local-attacker .kf-team-ct,
      #kill-feed .kf-local-attacker .kf-team-t { filter: brightness(1.22); }
      #kill-feed .kf-local-victim { opacity: 0.96; }

      /* Weapon icon — same warm-white art as the HUD. Sized by a contain box
         (max-width dominant) so a long AWP and a chunky pistol read at a
         balanced size instead of the AWP towering by height. */
      #kill-feed .kf-weap { display: inline-flex; align-items: center; }
      #kill-feed .kf-weap .hud-wic-img,
      #kill-feed .kf-weap .hud-nic-img {
        max-width: 46px; max-height: 20px; width: auto; height: auto; object-fit: contain;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
      }
      #kill-feed .kf-weap .hud-wic { max-width: 44px; max-height: 18px; width: auto; height: auto; }
      #kill-feed .kf-weap .hud-nic { max-width: 26px; max-height: 24px; width: auto; height: auto; }

      /* Modifier badges — warm-white glyphs on the weapon; larger than the
         gun so the headshot / smoke / blind / no-scope reads at a glance. */
      #kill-feed .kf-badges { display: inline-flex; align-items: center; gap: 6px; }
      #kill-feed .kf-badge-img {
        height: 26px; width: auto; object-fit: contain;
        filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
      }
    `;
    document.head.appendChild(style);
  }
}

/** A fully-resolved kill row (names + teams + flags), ready to render. */
interface RenderEntry {
  attackerName: string;
  attackerTeam: "ct" | "t";
  victimName: string;
  victimTeam: "ct" | "t";
  weaponId: string;
  headshot: boolean;
  throughSmoke: boolean;
  blindKill: boolean;
  noScope: boolean;
  suicide: boolean;
  isLocalAttacker: boolean;
  isLocalVictim: boolean;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
